/**
 * AI Recruit - Proctored Interview Public (Candidate) API Service
 * Plain axios client, deliberately NOT the `apiV2` instance — this flow is
 * unauthenticated (the token in the URL is the credential) and must never
 * go through the auth-token/401-refresh/logout interceptors `apiV2` has.
 */

import axios from 'axios'
import { API_V2_URL } from './api'

const publicApi = axios.create({
  baseURL: `${API_V2_URL}/interview/public`,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
})

export interface VerifyScreen {
  // 'incomplete' = this attempt was interrupted and has been closed. The link
  // stays valid but only ever shows the Assessment Interrupted screen; a
  // re-issued link is a different session with its own token.
  status: 'pending' | 'verified' | 'in_progress' | 'completed' | 'incomplete'
  candidate_name?: string | null
  job_title?: string | null
  company?: string | null
  question_count: number
  total_duration_minutes?: number | null
  support_contact?: SupportContact | null
}

export interface PublicTestCase {
  input: string
  expected_output: string
  description?: string
}

export interface PublicQuestion {
  id: string
  skill_name: string
  question_type_name: string
  question_type_category: string
  response_mode: 'verbal' | 'text' | 'code_editor' | 'whiteboard'
  question_subtype_name?: string | null
  question_text: string
  test_cases: PublicTestCase[]
  duration_minutes: number
  display_order: number
}

export interface PublicAnswerState {
  answer_text?: string | null
  answer_metadata: Record<string, any>
  marked_for_review: boolean
}

export interface PublicIntegrityEventState {
  type: string
  detected_at: string
}

export interface SessionState {
  status: 'pending' | 'verified' | 'in_progress' | 'completed'
  current_question_index: number
  questions: PublicQuestion[]
  // Resume support — populated with whatever was already saved/detected
  // for this session, so reopening the link after closing the tab
  // restores prior answers and the integrity-event history instead of
  // looking reset.
  answers: Record<string, PublicAnswerState>
  integrity_events: PublicIntegrityEventState[]
  // How often (seconds) the browser should upload the next recording chunk —
  // .env-configurable server-side (PROCTORED_INTERVIEW_RECORDING_CHUNK_INTERVAL_SECONDS).
  recording_chunk_interval_seconds?: number
}

// Follow-up details shown on the Assessment Interrupted screen. Only sent
// for status === 'incomplete'; either field may be absent if the
// organization has not configured it.
export interface SupportContact {
  phone?: string | null
  email?: string | null
}

export const getVerifyScreen = (token: string) =>
  publicApi.get<VerifyScreen>(`/${token}`).then((r) => r.data)

export const verifyCandidate = (token: string, confirmed = true) =>
  publicApi.post<VerifyScreen>(`/${token}/verify`, { confirmed }).then((r) => r.data)

export interface ProctoringChecklistPayload {
  browser_geo?: { lat: number; lng: number; accuracy_meters?: number } | null
  extra_monitor_detected?: boolean | null
  declaration_confirmed: boolean
  camera_mic_verified?: boolean
  camera_snapshot?: string | null
}

export const startInterview = (token: string, proctoring?: ProctoringChecklistPayload) =>
  publicApi.post<SessionState>(`/${token}/start`, proctoring || { declaration_confirmed: false }).then((r) => r.data)

export interface ProctoringViolationResult {
  violations: number
  auto_submitted: boolean
}

export const reportProctoringViolation = (token: string, type: string = 'fullscreen_exit') =>
  publicApi
    .post<ProctoringViolationResult>(`/${token}/proctoring/violation`, { type })
    .then((r) => r.data)

export const getSessionState = (token: string) =>
  publicApi.get<SessionState>(`/${token}/state`).then((r) => r.data)

export const submitAnswer = (
  token: string,
  questionId: string,
  data: { answer_text?: string; answer_metadata?: Record<string, any>; time_spent_seconds?: number }
) => publicApi.post(`/${token}/answers/${questionId}`, data).then((r) => r.data)

// Best-effort save of the current question's draft right as the tab is
// closing (window 'beforeunload') — sendBeacon is the only API guaranteed
// to let a request complete after the page starts unloading; a normal
// axios/fetch call started here would very likely be aborted mid-flight.
// No token in an Authorization header is needed (this public API embeds
// the token in the URL path), so a raw Blob POST works without axios.
export const submitAnswerBeacon = (
  token: string,
  questionId: string,
  data: { answer_text?: string; answer_metadata?: Record<string, any>; time_spent_seconds?: number }
): void => {
  const url = `${publicApi.defaults.baseURL}/${token}/answers/${questionId}`
  try {
    const sent = navigator.sendBeacon(url, new Blob([JSON.stringify(data)], { type: 'application/json' }))
    // sendBeacon FAILS SILENTLY (returns false, does not throw) when the
    // payload exceeds the browser's beacon size quota (commonly ~64KB) —
    // a whiteboard sketch's base64 image easily crosses that. Retry once
    // without the image so the text/mark-for-review/time at least survive,
    // even if the drawing itself doesn't fit in this one last request.
    const metadata = data.answer_metadata
    if (!sent && metadata && metadata.whiteboard_image_data) {
      const { whiteboard_image_data, ...restMetadata } = metadata
      navigator.sendBeacon(url, new Blob([JSON.stringify({ ...data, answer_metadata: restMetadata })], { type: 'application/json' }))
    }
  } catch {
    // Best-effort only — sendBeacon can throw in rare cases (e.g. an
    // unsupported browser) but there's nothing more to do at this point.
  }
}

export const completeInterview = (token: string) =>
  publicApi.post<{ status: string }>(`/${token}/complete`).then((r) => r.data)

export interface RunCodeResult {
  stdout: string
  stderr: string
  exit_code?: number | null
  error?: string | null
  // How many Run/Run Test Cases attempts are now recorded for this question
  // (used for process-based scoring), and the cap before the oldest ones
  // start getting dropped — surfaced in the UI so the limit isn't invisible.
  run_history_count?: number | null
  run_history_limit?: number
}

export interface TestCaseRunResult {
  description?: string
  passed: boolean
  explanation?: string
}

export interface RunTestCasesResult {
  results: TestCaseRunResult[]
  run_history_count?: number | null
  run_history_limit?: number
}

// AI-simulated (the LLM traces the code and reports what it would output) —
// there's no real sandboxed execution engine in this app. See the backend
// endpoint's docstring for why (the public Piston API this originally
// targeted went offline).
export const runCode = (token: string, questionId: string, code: string, language: string, stdin: string = '') =>
  publicApi.post<RunCodeResult>(`/${token}/questions/${questionId}/run`, { code, language, stdin }).then((r) => r.data)

export const runTestCases = (token: string, questionId: string, code: string, language: string) =>
  publicApi
    .post<RunTestCasesResult>(`/${token}/questions/${questionId}/run-tests`, { code, language })
    .then((r) => r.data)

// Client-side ML (face/object detection, audio-level heuristics) flags one
// of these — logged separately from fullscreen/tab-switch violations above
// since these are best-effort signals and never auto-submit the interview.
export type IntegrityEventType =
  | 'multiple_persons' | 'no_person' | 'electronic_device'
  | 'looking_away' | 'camera_blocked' | 'multiple_audio_sources'

export const reportIntegrityEvent = (token: string, type: IntegrityEventType, details: Record<string, any> = {}) =>
  publicApi.post(`/${token}/integrity-event`, { type, details }).then((r) => r.data)

// One combined screen+mic recording for the whole interview, uploaded once
// at completion (see useMediaRecorder / interviewScreenShare — the same
// capture mechanics the AI-interview module already uses).
export const uploadRecording = (token: string, blob: Blob) => {
  const formData = new FormData()
  formData.append('recording', blob, 'recording.webm')
  return publicApi
    .post(`/${token}/recording`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })
    .then((r) => r.data)
}

// Optional experience feedback left on the thank-you screen. `tags` are the
// stable keys from FEEDBACK_TAGS (see PublicInterviewPage.tsx), not the
// displayed wording. Re-submitting overwrites the previous answer.
export interface FeedbackPayload {
  rating: number
  tags: string[]
  comment?: string
}

export const submitFeedback = (token: string, feedback: FeedbackPayload) =>
  publicApi.post<{ status: string }>(`/${token}/feedback`, feedback).then((r) => r.data)

// Ends an in-progress attempt immediately as incomplete, because the
// candidate left the assessment. Sent via sendBeacon so it survives page
// teardown — a normal request started during unload would very likely be
// aborted. The server ignores it unless the session is actually in progress.
export const abandonInterviewBeacon = (token: string, reason: string): void => {
  const url = `${publicApi.defaults.baseURL}/${token}/abandon`
  try {
    navigator.sendBeacon(url, new Blob([JSON.stringify({ reason })], { type: 'application/json' }))
  } catch {
    // Best-effort. The server-side backstop (reopening the link on an
    // already-started attempt ends it) covers a beacon that never lands.
  }
}

// Same thing, but for the in-page case where the tab is NOT closing — the
// candidate has simply been outside fullscreen too long. A normal request is
// fine here and, unlike a beacon, tells us it worked.
export const abandonInterview = (token: string, reason: string) =>
  publicApi.post<{ status: string }>(`/${token}/abandon`, { reason }).then((r) => r.data)
